import React from 'react';

/**
 * ManagedDisclaimerBlock: report block whose text comes from Admin > Disclaimers.
 *
 * The report payload carries `managed_disclaimers` ({key: {text} | {error}}),
 * already resolved server-side for the viewer's brand and this portfolio, so
 * view mode, the WYSIWYG PDF and bulk PDFs all print the same text.
 * The text is not editable here; only the font size (a layout concern) is.
 * A missing or failed disclaimer renders a visible error, never blank.
 *
 * blockDef: { type: 'text', managedDisclaimer: <key>, fontSize, label }
 *   The block stays type 'text' with its old wording in custom_content, so a
 *   frontend that predates managed disclaimers still prints that snapshot.
 *   This frontend ignores the snapshot and prints the grid text.
 * Legacy form { type: 'disclaimer', disclaimerKey } is also accepted.
 */
const ERROR_STYLE = {
  color: '#F26B43', border: '1px dashed #F26B43', padding: '4px 6px', fontSize: '8pt',
};

const FONT_SIZES = ['6pt', '6.5pt', '7pt', '7.5pt', '8pt', '9pt', '10pt'];

const renderText = (text) => {
  if (/<[a-z][\s\S]*>/i.test(text)) {
    return <div dangerouslySetInnerHTML={{ __html: text }} />;
  }
  return text.split(/\n\n+/).map((para, i) => (
    <p key={i}>
      {para.split('\n').map((line, j, arr) => (
        <React.Fragment key={j}>
          {line}
          {j < arr.length - 1 && <br />}
        </React.Fragment>
      ))}
    </p>
  ));
};

const ManagedDisclaimerBlock = ({ blockId, blockDef, managed, editMode, onBlockDefChange }) => {
  const key = blockDef?.managedDisclaimer || blockDef?.disclaimerKey;
  const fontSize = blockDef?.fontSize || '7pt';
  const entry = key ? managed?.[key] : null;

  let body;
  if (!key) {
    body = <div className="managed-disclaimer-error" style={ERROR_STYLE}>Disclaimer block has no disclaimer type set.</div>;
  } else if (!managed) {
    body = <div className="managed-disclaimer-error" style={ERROR_STYLE}>Report data has no managed disclaimers (backend not updated?).</div>;
  } else if (!entry) {
    body = <div className="managed-disclaimer-error" style={ERROR_STYLE}>Unknown disclaimer type "{key}".</div>;
  } else if (entry.error) {
    body = <div className="managed-disclaimer-error" style={ERROR_STYLE}>{entry.error}</div>;
  } else {
    body = renderText(entry.text || '');
  }

  return (
    <div className="section-box managed-disclaimer">
      {editMode && (
        <div
          className="managed-disclaimer-toolbar"
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
            fontSize: '8pt', color: '#337B91', fontFamily: 'inherit',
          }}
        >
          <span>Disclaimer ({blockDef?.label || key}) is managed in Admin › Disclaimers</span>
          <a href="/admin?tab=disclaimers" target="_blank" rel="noopener noreferrer" style={{ color: '#012341' }}>
            Edit text
          </a>
          <label style={{ marginLeft: 'auto' }}>
            Size{' '}
            <select
              value={fontSize}
              onChange={(e) => onBlockDefChange?.(blockId, { ...blockDef, fontSize: e.target.value })}
            >
              {FONT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>
      )}
      <div className="qr-body managed-disclaimer-text" style={{ fontSize }}>{body}</div>
    </div>
  );
};

export default ManagedDisclaimerBlock;
